// Four months of paper trail, for /demo.
//
// The pad's own record — what it PROPOSED and what the market did next — takes
// a morning a day to exist, because a day of it is written by the settle at
// 07:00 the following morning and never retrospectively. So on /demo there was
// nothing: History's Proposed tab was empty, every strategy card said "Too few
// to tell", and the screens whose whole job is to show what the pad has
// actually done showed a person deciding whether to install exactly nothing.
//
// This is that season, invented. Everything in here is made up — the results,
// the closing prices and the CLV — from the same clubs the demo board prints on
// Today, so the two screens belong to each other.
//
// A FORTNIGHT WAS NOT A SEASON. The trail used to run fourteen days, and at
// fourteen days Insights could not do its own job: "All" was the same window as
// "30d", the 30d delta chip had no previous thirty days to compare against, the
// twelve-week calendar was one full column and eleven empty ones, and no window
// ever held the three hundred priced legs the verdict needs, so the whole
// screen said "Early days" whatever you asked it. A hundred and twenty days is
// what makes those four things answerable — and it is also past the sixty days
// the real trail keeps, which is the point at which the all-time rollup rather
// than the trail itself is what Insights is reading.
//
// It is a FIXTURE, not a feed, and it obeys demo-history.js's rule: one seeded
// mulberry32 per day drives every draw and nothing reads the clock, so two
// calls at the same `nowMs` are deeply equal. A demo whose CLV moved every time
// the screen was opened would teach the one lesson this app exists to unteach.
//
// The shape is `settleProposal`'s output, because it IS `settleProposal`'s
// output: the proposals are built here and settled by the same function the
// real trail goes through, so a screen can never be reading a shape the app
// does not produce.

import { mulberry32 } from './demo-history.js';
import { DAY, TRAIL_LINKED, demoMultiId } from './demo.js';
import { localDate } from './tickets.js';
import { tierFor } from './tier.js';
import { settleProposal, shiftDate } from './paper-trail.js';
import { CLASSIC } from './engine/strategies/classic.js';
import { DIXON_COLES_EDGE, ELO_EDGE } from './engine/strategies/catalogue.js';

const SYDNEY = 'Australia/Sydney';
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/**
 * How many mornings of trail the demo carries. Evidence looks back thirty,
 * Insights' longest window is all of it, and its calendar draws twelve weeks —
 * so a hundred and twenty is the first number that fills every one of them and
 * still leaves a full previous thirty days behind the thirty-day window.
 */
export const TRAIL_DAYS = 120;

/** What the pad stakes on every ticket it prints. */
const STAKE = 10;

const round2 = (n) => Math.round(n * 100) / 100;
const round4 = (n) => Math.round(n * 10_000) / 10_000;

/** A team's id in a match key: the name, lowercased, hyphen-joined. */
const slug = (name) => String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * The engine's own id for a competition the demo board names in full. A leg
 * says which sport it is in the engine's terms — that is what `sportsToSettle`
 * and `history-view`'s sport column are both written against.
 */
const SPORT_ID = {
  AFL: 'afl',
  NRL: 'nrl',
  NBL: 'nbl',
  EPL: 'epl',
  'Big Bash': 'bbl',
  'A-League': 'aleague',
};

/** How many legs each DESIGN prints, and where in the day's price ladder it starts.
 *  The word on the left is the design — Classic's four ways — and NOT the risk
 *  the ticket prints: that is `tierFor` on what the legs multiply out to, the
 *  same as on a real morning. See lib/tier.js. */
const TIERS = [
  ['low', 2, 0],
  ['medium', 3, 2],
  ['high', 4, 4],
  ['outrageous', 6, 8],
];

/**
 * A day's seed. The date's own digits, so it is stable forever and needs no
 * hash — and so two days running never share a sequence.
 */
const seedFor = (date) => Number(String(date).replace(/-/g, ''));

// --- the season ---------------------------------------------------------------
//
// A hundred and twenty days of the same day is not a longer demo, it is the
// same demo four times over: every window reads the same, the calendar is a
// solid block, the equity curve is a straight line and the scoreboard has
// nothing to say about one strategy against another. So the trail has a SHAPE,
// and every dial below is keyed on a day's AGE — 1 is yesterday — because that
// is what puts a given stretch of it inside or outside the window a reader is
// looking through.
//
// Nothing here reads the clock: the age comes from the date list
// `demoProposalOutcomes` builds out of the `nowMs` it was handed, so the same
// moment still gives the same season, byte for byte.

/** Mornings the pad sat out. No board worth dealing, so no proposals at all. */
const BLANK_AGES = new Set([47, 48, 68]);

/** Thin boards: one ranked pick got up and no Classic set was worth printing. */
const QUIET_AGES = new Set([33, 41, 55, 76]);

/** Half a board: the two short sets and a pick. */
const THIN_AGES = new Set([35, 36, 44, 52, 61, 79]);

/**
 * The arcs. Ages count BACK from yesterday, so the older bound is the start:
 * Elo Edge runs hot for a fortnight and then spends the next fortnight cold,
 * and somewhere behind both of them Classic has a week and a half where nothing
 * lands at all.
 *
 * A demo where the pad only ever drifts gently upward is the demo this app
 * exists not to be. The slump is there to be found.
 */
const ELO_HOT = [22, 35];
const ELO_COLD = [8, 21];
const CLASSIC_SLUMP = [57, 66];

const inArc = (age, [from, to]) => age >= from && age <= to;

/**
 * How much better or worse than the price a strategy is running. 1 is the
 * honest reading — a $1.30 favourite lands about three times in four — and the
 * arcs push it either side of that.
 */
const formFor = (age) => ({
  elo: inArc(age, ELO_HOT) ? 1.08 : inArc(age, ELO_COLD) ? 0.7 : 1,
  classic: inArc(age, CLASSIC_SLUMP) ? 0.55 : 1,
});

/**
 * What the arcs do to the closing line as well as to the results, because a
 * strategy that only ever runs hot on the scoreboard and never on the close is
 * a strategy running hot on luck — and CLV is the number this screen is for.
 * Small, and it nets out to barely anything over the whole four months: the
 * trail still claims about a point, which is all an honest demo may claim.
 */
const clvTiltFor = (age) => {
  if (inArc(age, ELO_HOT)) return 0.008;
  if (inArc(age, ELO_COLD)) return -0.006;
  if (inArc(age, CLASSIC_SLUMP)) return -0.01;
  return 0;
};

/** Monday is 0, so Saturday and Sunday are the two at the end. */
const weekdayOf = (date) => (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;

/**
 * A weekend's SECOND deal. Saturday's board is big enough to be dealt twice,
 * so it is — the two shorter tiers, cut one rung deeper into the same price
 * ladder so the second set is a different four tickets and not the first four
 * printed again.
 */
const SECOND_DEAL = [['low', 2, 1], ['medium', 3, 3]];

/**
 * What the pad prints on one morning, or null on a morning it sat out.
 *
 * WEEKENDS ARE BUSIER, because the board is: Saturday and Sunday are dealt
 * twice, a weekday once. That is the difference the calendar heatmap is
 * drawing — without it every cell in the grid is the same shade and the grid is
 * decoration.
 *
 * EVERY ORDINARY DAY PRINTS ALL FOUR TIERS, though. A pad that skipped the long
 * sets on weekdays put fewer than a hundred priced legs inside the seven-day
 * window, and under a hundred the verdict is not "early days", it is "too few
 * to tell" — which is the answer the fortnight already gave and the whole
 * reason this fixture grew.
 */
function dayPlan(date, age, rand) {
  if (BLANK_AGES.has(age)) return null;
  const weekend = weekdayOf(date) >= 5;
  const quiet = QUIET_AGES.has(age);
  const thin = THIN_AGES.has(age);
  return {
    deals: quiet ? [] : thin ? [TIERS.slice(0, 2)] : weekend ? [TIERS, SECOND_DEAL] : [TIERS],
    // Both ranked picks on any day with a board worth ranking — a weekday
    // still has a Premier League fixture on it, which is what Dixon-Coles is
    // fitted to. A quiet morning finds one, and a half-board is the coin toss
    // the trail always had.
    picks: quiet ? 1 : thin ? (rand() < 0.5 ? 1 : 2) : 2,
    form: formFor(age),
    clvTilt: clvTiltFor(age),
  };
}

/**
 * Every match on the demo board, as a leg proposed on `date`, priced and
 * closed. Sorted shortest price first: that ladder is what makes a `low`
 * ticket short and an `outrageous` one long, the same way the real tiers are
 * cut out of a real board.
 *
 * The closing price is drawn to leave CLV SLIGHTLY POSITIVE on average — about
 * a point — which is the most an honest demo may claim: enough that the number
 * is worth printing, nowhere near enough for the verdict to say "beats the
 * close" at fourteen days of sample.
 */
function legPool(date, rand, clvTilt = 0) {
  const legs = DAY.map(([home, away, title, , , homePrice, awayPrice]) => {
    const onHome = rand() < 0.78;
    const team = onHome ? home : away;
    const base = onHome ? homePrice : awayPrice;
    const price = round2(Math.max(1.05, base * (0.96 + rand() * 0.08)));

    // WHAT THE MARKET SETTLED AT IS A PROBABILITY, not a price (engine review
    // #6). The demo draws the CLV it is claiming, turns it into the devigged
    // closing probability that would produce it, and marks THAT up by three
    // points of margin to get the price the punter's own book shows as the
    // close. So the two numbers on a demo leg are the two numbers a real leg
    // carries — the fair close the CLV is measured against, and the book's own
    // closing quote, which is shorter — and the CLV is read BACK off the
    // rounded pair, so the figure printed is exactly `clvFair`'s.
    const wanted = -0.012 + rand() * 0.045 + clvTilt;
    const closingFair = round4(Math.min(0.98, (1 + wanted) / price));
    const closingPrice = round2(Math.max(1.02, 0.97 / closingFair));
    const sport = SPORT_ID[title] ?? slug(title);
    const hour = 13 + Math.floor(rand() * 9);

    return {
      matchKey: `${sport}:${date}:${slug(home)}:${slug(away)}`,
      side: onHome ? 'home' : 'away',
      price,
      selection: `${team} to win`,
      match: `${home} v ${away}`,
      sport,
      // +10:00 is Sydney's standard offset. A fixture does not need a timezone
      // table: an hour out in daylight saving still lands on the same calendar
      // day, which is the only thing anything reads off a kickoff here.
      kickoff: `${date}T${String(hour).padStart(2, '0')}:00:00+10:00`,
      closingPrice,
      closingFair,
      // The exchange priced every match on the demo board, so every demo leg is
      // measured against it — which is the best case the real app has, and the
      // one a demo should show.
      clvSource: 'exchange',
      clv: closingFair * price - 1,
      result: null,
    };
  });
  return legs.sort((a, b) => a.price - b.price || a.matchKey.localeCompare(b.matchKey));
}

/**
 * How a leg finished. The chance is the price's own — a $1.30 favourite lands
 * about three times in four — so a two-leg ticket wins often and a six-leg one
 * almost never, without anybody having to decide that per ticket.
 *
 * `form` is the season's dial (see `formFor`): above one the strategy is
 * running ahead of the price, below it behind. Capped short of certainty,
 * because nothing in this app is ever allowed to look like a sure thing.
 */
function decide(leg, rand, form = 1) {
  if (rand() < 0.02) return 'void';
  return rand() < Math.min(0.95, form / leg.price) ? 'won' : 'lost';
}

/** The ticket header, derived from its legs the way a real one is. */
function multiFor(id, design, date, legs, strategy) {
  const combinedOdds = round2(legs.reduce((acc, l) => acc * l.price, 1));
  const combinedProbability = round4(legs.reduce((acc, l) => acc / l.price, 1));
  return {
    id,
    // Exactly as the engine prints one: the design it was cut to, and the risk
    // word read off the chance. A demo that wrote its own tiers would be a demo
    // of an app that does not exist.
    ...(design ? { design } : {}),
    tier: tierFor(combinedProbability),
    forDate: date,
    status: 'proposed',
    stake: STAKE,
    bookmaker: 'Sportsbet',
    oddsFetchedAt: `${date}T09:12:00+10:00`,
    combinedOdds,
    combinedProbability,
    potentialReturn: round2(STAKE * combinedOdds),
    label: strategy.name,
    strategyId: strategy.id,
  };
}

/**
 * One day of the pad: the Classic sets the day's plan calls for, and the one or
 * two picks that ranked. Null on a morning the pad sat out.
 */
function proposalsOn(date, rand, age) {
  const plan = dayPlan(date, age, rand);
  if (!plan) return null;
  const pool = legPool(date, rand, plan.clvTilt);
  const out = [];

  plan.deals.forEach((deal, setIndex) => {
    for (const [tier, count, from] of deal) {
      const legs = pool.slice(from, from + count).map((leg) => ({ ...leg, result: decide(leg, rand, plan.form.classic) }));
      if (legs.length < count) continue;
      // A re-deal's tickets carry the deal in their id, because the first set
      // and the second both print a `low` and two tickets on one morning may
      // not share an id — History keys its rows on exactly this.
      const id = setIndex ? `demo-trail-${date}-${setIndex + 1}-${tier}` : `demo-trail-${date}-${tier}`;
      out.push({
        id: `${date}:set:${id}`,
        date,
        source: 'set',
        setIndex,
        strategyId: CLASSIC.id,
        strategyName: CLASSIC.name,
        multi: multiFor(id, tier, date, legs, CLASSIC),
        legs,
        pricesFrom: `${date}T09:12:00+10:00`,
      });
    }
  });

  // The picks: the day's ranking, which prints at most four and on a quiet
  // board finds one. Elo takes the codes it is fitted on, Dixon-Coles the
  // Premier League — the same filters the catalogue declares, so a demo pick
  // is never a ticket the real strategy could not have printed.
  const pickStrategies = [ELO_EDGE, DIXON_COLES_EDGE].slice(0, plan.picks);
  pickStrategies.forEach((strategy, i) => {
    const eligible = pool.filter((l) => strategy.filters.sports.includes(l.sport));
    const form = strategy.id === ELO_EDGE.id ? plan.form.elo : 1;
    const legs = eligible.slice(0, 2).map((leg) => ({ ...leg, result: decide(leg, rand, form) }));
    if (!legs.length) return;
    const id = `demo-trail-${date}-${strategy.id}`;
    out.push({
      id: `${date}:pick:${id}`,
      date,
      source: 'pick',
      rank: i + 1,
      superseded: false,
      strategyId: strategy.id,
      strategyName: strategy.name,
      // A pick has no DESIGN — it is a strategy's one ticket, not one of four
      // ways — and it carries a tier like everything else, because a tier is a
      // reading of the chance and every ticket has one of those.
      multi: multiFor(id, null, date, legs, strategy),
      legs,
      pricesFrom: `${date}T09:12:00+10:00`,
    });
  });

  return out;
}

/**
 * Yesterday's two loose ends.
 *
 * A trail where every single ticket has an answer is not what a trail looks
 * like on any real morning, and the two ways of having no answer read
 * DIFFERENTLY on the screens — "Pending" is a game still to come, "No result"
 * is a score that should have arrived and did not — so the demo has to carry
 * one of each or half of History's vocabulary is never seen.
 *
 * Both are made by taking the result off one leg and dating it: two hours ago
 * is a match the settle has not been past yet, two days ago is one it has. The
 * other legs are forced to `won` because a single lost leg decides the whole
 * ticket whatever the rest are waiting on — the ticket would settle as lost and
 * neither loose end would exist.
 */
function loosen(proposal, kickoffMs) {
  const legs = proposal.legs.map((leg, i) => (i === proposal.legs.length - 1
    ? { ...leg, result: null, kickoff: new Date(kickoffMs).toISOString() }
    : { ...leg, result: 'won' }));
  return { ...proposal, legs };
}

/**
 * The demo's paper trail: `{ 'YYYY-MM-DD': [settled proposal] }`, a hundred and
 * twenty Sydney days ending yesterday — exactly what `stores/settlement.js`
 * writes into `prefs.proposalOutcomes` and `stores/paper-trail.js` reads back.
 *
 * Ending YESTERDAY because that is the trail's own rule: today's pad is still
 * being dealt and is settled tomorrow morning. Sydney days because the pad is
 * printed on Sydney days wherever the phone happens to be.
 *
 * A MORNING THE PAD SAT OUT HAS NO KEY, rather than an empty list. That is what
 * the real trail does — settlement writes a day when it settles one — and it is
 * what makes the calendar's blank cells blank rather than zero-shaded.
 */
export function demoProposalOutcomes(nowMs = Date.now()) {
  const today = localDate(nowMs, SYDNEY);
  const nowIso = new Date(nowMs).toISOString();
  const dates = [];
  for (let i = TRAIL_DAYS; i >= 1; i -= 1) dates.push(shiftDate(today, -i));

  const byDate = new Map();
  dates.forEach((date, i) => {
    const list = proposalsOn(date, mulberry32(seedFor(date)), TRAIL_DAYS - i);
    if (list?.length) byDate.set(date, list);
  });

  // Yesterday's loose ends, on the two LONGEST tickets — the ones a reader is
  // least surprised to find still open. By leg count rather than by tier,
  // because yesterday is not guaranteed to be a weekend and a weekday pad has
  // no outrageous set on it to be the second one.
  const last = byDate.get(dates[dates.length - 1]) ?? [];
  const longest = last
    .map((p, at) => ({ at, legs: p.legs.length }))
    .sort((a, b) => b.legs - a.legs || a.at - b.at);
  if (longest[0]) last[longest[0].at] = loosen(last[longest[0].at], nowMs - 2 * HOUR_MS);
  if (longest[1]) last[longest[1].at] = loosen(last[longest[1].at], nowMs - 2 * DAY_MS);

  // THE JOIN. Eight of the tickets the demo history has already placed were
  // proposed here first, so `multi.id` matches on both sides and the
  // proposed → placed → won funnel has a middle band. The placed rows are
  // dated in the DEVICE's zone and the trail in Sydney, so the day is matched
  // rather than assumed: within a day of each other, always, and the nearest
  // one is the right one.
  TRAIL_LINKED.forEach(([daysAgo, design], i) => {
    const betId = demoMultiId(daysAgo, design, nowMs);
    const betDate = betId.slice('demo-'.length, 'demo-'.length + 10);
    const date = dates.reduce((best, d) =>
      (Math.abs(Date.parse(d) - Date.parse(betDate)) < Math.abs(Date.parse(best) - Date.parse(betDate)) ? d : best));
    const list = byDate.get(date) ?? [];
    // Every other link is a Classic ticket of that design; the ones between are
    // the day's top pick, which is the other half of the join worth showing — a
    // strategy the punter backed because it ranked, not because it was on the
    // pad. Alternating rather than one of each, so a funnel drawn over any of
    // the three windows has both kinds in it.
    const at = i % 2 === 0
      // Matched on the DESIGN: two of the four ways can print the same risk
      // word now, and a join made on the word would link the wrong ticket.
      ? list.findIndex((p) => p.source === 'set' && p.multi.design === design)
      : list.findIndex((p) => p.source === 'pick');
    if (at < 0) return;
    const was = list[at];
    // The proposal's own id is `${date}:${source}:${multi.id}` — proposals.js
    // builds it that way and History keys rows on it, so it moves with the id
    // it is made of.
    list[at] = {
      ...was,
      id: `${date}:${was.source}:${betId}`,
      multi: { ...was.multi, id: betId },
    };
  });

  const out = {};
  for (const [date, list] of byDate) {
    // Settled by the real function, with no closing rows and no results to add:
    // every leg here is already stamped, so `resolveLeg` passes each one
    // straight back and what comes out is the ticket's own arithmetic —
    // outcome, CLV, stake and return — computed exactly as the app computes it.
    out[date] = list.map((p) => settleProposal(p, [], [], nowIso));
  }
  return out;
}
